"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkedDataConformanceMaintainer = void 0;
const datasheet_1 = require("./datasheet");
const lodash_1 = require("lodash");
const field_types_1 = require("../types/field_types");
const store_1 = require("../exports/store");
const utils_1 = require("./utils");
var ActionFlag;
(function (ActionFlag) {
    ActionFlag["Add"] = "+";
    ActionFlag["Del"] = "-";
})(ActionFlag || (ActionFlag = {}));
/**
 * a common class that use to maintain relation fields' data consistency
 * when CRUD to record, if there's relation fields in record, need to update relation field's records at the same time.
 */
class LinkedDataConformanceMaintainer {
    constructor() {
        this.linkedChange = new Map();
    }
    /**
     * compare value and oldValue, auto calculate the changelog of relation records, and mark them down.
     * during one command cycle, can call insert many times.
     */
    insert(state, linkedSnapshot, recordId, field, value, oldValue) {
        const { brotherFieldId = field.id, foreignDatasheetId } = field.property;
        if (!value && !oldValue) {
            return;
        }
        // in `value` , but not in `oldValue`, need to add
        const toAdd = (0, lodash_1.without)(value, ...(oldValue || []));
        // oldValue exists,  but value don't exist, need to delete
        const toDel = (0, lodash_1.without)(oldValue, ...(value || []));
        toAdd.forEach(linkedRecordId => {
            this.insertLinkedRecordChange(foreignDatasheetId, brotherFieldId, linkedRecordId, recordId, ActionFlag.Add);
        });
        toDel.forEach(linkedRecordId => {
            const cellValueInLinkedCell = store_1.Selectors.getCellValue(state, linkedSnapshot, linkedRecordId, brotherFieldId, undefined, undefined, true);
            if (!cellValueInLinkedCell) {
                console.error(`The content of the related-record is empty, this record: ${recordId}`);
                return;
            }
            if (!cellValueInLinkedCell.includes(recordId)) {
                console.error(`No records in this table were found in the related-records, this record: ${recordId} related-record ${cellValueInLinkedCell}`);
                return;
            }
            this.insertLinkedRecordChange(foreignDatasheetId, brotherFieldId, linkedRecordId, recordId, ActionFlag.Del);
        });
    }
    insertLinkedRecordChange(datasheetId, brotherFieldId, linkedRecordId, recordId, actionFlag) {
        let datasheet = this.linkedChange.get(datasheetId);
        if (!datasheet) {
            datasheet = new Map();
            this.linkedChange.set(datasheetId, datasheet);
        }
        let brotherField = datasheet.get(brotherFieldId);
        if (!brotherField) {
            brotherField = new Map();
            datasheet.set(brotherFieldId, brotherField);
        }
        let linkedRecord = brotherField.get(linkedRecordId);
        if (!linkedRecord) {
            linkedRecord = { add: new Set(), del: new Set() };
            brotherField.set(linkedRecordId, linkedRecord);
        }
        if (actionFlag === ActionFlag.Add) {
            linkedRecord.add.add(recordId);
        }
        if (actionFlag === ActionFlag.Del) {
            linkedRecord.del.add(recordId);
        }
    }
    /**
     * Batch convert value changes in the associated table into LinkedActions.
     * This method will clear the value change cache. In a command cycle, it can only be executed once.
     */
    flushLinkedActions(state) {
        const linkedActions = [];
        if (this.linkedChange.size > 0) {
            this.linkedChange.forEach((datasheet, datasheetId) => {
                const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
                const linkedAction = { datasheetId, actions: [] };
                linkedActions.push(linkedAction);
                datasheet.forEach((field, fieldId) => {
                    field.forEach((changeIds, recordId) => {
                        if (!snapshot) {
                            console.error(`${datasheetId} for snapshot is not exit`);
                            return;
                        }
                        if (!snapshot.recordMap[recordId]) {
                            console.error(`record: ${recordId} in datasheet: ${datasheetId} does not exist！`);
                            return;
                        }
                        const fieldType = snapshot.meta.fieldMap[fieldId] && snapshot.meta.fieldMap[fieldId].type;
                        // Make sure that the cell is populated only when the foreign key field is indeed the relation field type.
                        const cellValueInLinkedCell = fieldType === field_types_1.FieldType.Link ?
                            store_1.Selectors.getCellValue(state, snapshot, recordId, fieldId, undefined, undefined, true) || [] : [];
                        let newLinkedCellValue = (0, lodash_1.without)(cellValueInLinkedCell, ...changeIds.del);
                        newLinkedCellValue.push(...changeIds.add);
                        newLinkedCellValue = (0, utils_1.handleEmptyCellValue)(newLinkedCellValue, field_types_1.BasicValueType.Array);
                        const action = datasheet_1.DatasheetActions.setRecord2Action(snapshot, { recordId, fieldId, value: newLinkedCellValue });
                        action && linkedAction.actions.push(action);
                    });
                });
            });
        }
        this.linkedChange.clear();
        return linkedActions;
    }
}
exports.LinkedDataConformanceMaintainer = LinkedDataConformanceMaintainer;
//# sourceMappingURL=linked_data_conformance_maintainer.js.map